import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faExclamationTriangle,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./Forgotpassword.module.css";
import logoBg from "../static/logobgr.png";
import clinicLogo from "../static/logo.png";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isValidToken, setIsValidToken] = useState(true);

  const API_BASE = "/api";
  const token = searchParams.get("token");
  const emailParam = searchParams.get("email");

  useEffect(() => {
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
    if (!token) {
      setIsValidToken(false);
    }
  }, [token, emailParam]);

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters!");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/patientauth/reset-password`, {
        email,
        token,
        newPassword,
      });

      if (response.data.status) {
        toast.success("Password reset successful! Redirecting to login...");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        toast.error(response.data.message || "Failed to reset password");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Error resetting password. Try again later."
      );
      console.error("Error:", error);
    }
    setLoading(false);
  };

  return (
    <div className={styles.authContainer}>
      {/* ─── 1. LEFT COLUMN (The Visual Anchor - Desktop Only) ───────── */}
      <div className={styles.leftCol}>
        <div className={styles.leftOverlay} />

        <div className={styles.leftContentTop}>
          <Link to="/" className={styles.brandLogo}>
            <img src={logoBg} alt="ReformMe Logo" className={styles.leftLogoImg} />
            <span>ReformMe Healthcare</span>
          </Link>
        </div>

        <div className={styles.leftContentBottom}>
          <div className={styles.quoteWrap}>
            <p className={styles.quoteText}>
              “Securely establish your new credentials to continue your recovery
              journey with ReformMe Healthcare.”
            </p>
            <p className={styles.quoteAuthor}>
              Encrypted Patient Portal • Gurugram
            </p>
          </div>
        </div>
      </div>

      {/* ─── 2. RIGHT COLUMN (The Form Container) ────────────────────── */}
      <div className={styles.rightCol}>
        <div className={styles.formCard}>
          {!isValidToken ? (
            /* ─── INVALID / EXPIRED LINK VIEW ──────────────────────────── */
            <>
              <div className={styles.formHeader}>
                <div style={{ color: "#dc2626", fontSize: "2.5rem", marginBottom: "0.5rem" }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                </div>
                <h1 className={styles.headline}>Invalid Link</h1>
                <p className={styles.subheadline}>
                  This password reset link is invalid or has expired. Recovery links remain active for 30 minutes for your security.
                </p>
              </div>

              <div style={{ textAlign: "center", marginTop: "1rem" }}>
                <Link to="/forgot-password" className={styles.submitBtn} style={{ textDecoration: "none", display: "inline-flex" }}>
                  <span>Request a New Reset Link</span>
                </Link>
              </div>
            </>
          ) : (
            /* ─── VALID RESET PASSWORD FORM ────────────────────────────── */
            <>
              <Link to="/login" className={styles.backNavBtn}>
                <FontAwesomeIcon icon={faArrowLeft} />
                <span>Back to Sign In</span>
              </Link>

              <div className={styles.formHeader}>
                <Link to="/" className={styles.formBrandLogo}>
                  <img
                    src={clinicLogo}
                    alt="ReformMe Healthcare"
                    className={styles.clinicLogoImg}
                  />
                  <div className={styles.brandTextStack}>
                    <span className={styles.brandTextTop}>ReformMe</span>
                    <span className={styles.brandTextBottom}>Healthcare</span>
                  </div>
                </Link>
                <h1 className={styles.headline}>Create New Password</h1>
                <p className={styles.subheadline}>
                  Enter and confirm your new secure password for <strong style={{ color: "#1e293b" }}>{email || "your account"}</strong>.
                </p>
              </div>

              <form onSubmit={handleResetPassword} className={styles.form}>
                {email && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Account Email</label>
                    <input
                      type="email"
                      disabled
                      value={email}
                      className={styles.input}
                      style={{ backgroundColor: "#f1f5f9", cursor: "not-allowed", color: "#64748b" }}
                    />
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label htmlFor="newPassword" className={styles.label}>
                    New Password (Min. 6 characters)
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    required
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={styles.input}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="confirmPassword" className={styles.label}>
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    required
                    placeholder="Re-enter new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={styles.input}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={styles.submitBtn}
                >
                  <span>{loading ? "Updating Credentials..." : "Reset Password"}</span>
                  {!loading && <FontAwesomeIcon icon={faArrowRight} />}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;